import {
  clearAccessTokenCookieFromClient,
  clearRefreshTokenCookieFromClient,
  clearSession,
  clearSupervisionCookieFromClient,
  extractSession,
  persistSession,
  writeAccessTokenCookieFromClient,
} from '@app/utils/authCookies'
import { getOrCreateDeviceId } from '@app/utils/deviceId'
import { sleep } from '@app/utils/sleep'

import { analyticsEvent } from '@app/packages/analytics/declaration'
import { type AuthSessionSnapshot } from '@app/packages/api/AuthSessionManager'
import { deleteUsersById } from '@app/packages/api/v2/actions/users'
import { deleteAuthSessionsCurrent, postAuthLogin, postAuthPhoneRequest, postAuthTestLogin } from '@app/packages/api/v3/actions/auth'
import type { V3PhoneRequestPurpose, V3SessionResource } from '@app/packages/api/v3/types/lib'
import type { V3PostAuthLoginParams } from '@app/packages/api/v3/types/params'
import { resolveThunkSelector } from '@app/packages/thunkSelector/thunkSelector'

import { profileUserResultSelector } from '@app/store/selectors/profile'
import { testLoginUsersResultSelector } from '@app/store/slices/app_config'
import { clearFirebaseToken } from '@app/store/slices/firebase_token'
import { profileUserSelector } from '@app/store/slices/profile'
import {
  clearSupervisionSlice,
  clearV3SessionSlice,
  isSupervisedSelector,
  markSessionLost,
  markSessionTeardown,
  setV3SessionSlice,
  v3SessionSelector,
} from '@app/store/slices/session'
import { createThunk } from '@app/store/thunk'

import { getAnalyticsRouter } from './analytics'
import { callApi, callV2Api } from './api'
import { getAmplitude } from './contexts/Amplitude'
import { getAuthSession } from './contexts/authSession'
import { getCookies } from './contexts/cookies'
import { getFirebaseMessagingManager } from './contexts/firebaseMessagingManager'

/**
 * Stores a freshly minted v3 session: pushes the snapshot into the redux slice,
 * seeds the auth manager (so the next request needs no round-trip), and persists
 * the `kidsout__at` cookie. No `kidsout_session` is written — the JWT is the
 * credential.
 */
export function adoptV3Session(data: V3SessionResource) {
  return createThunk(async (dispatch): Promise<void> => {
    const snapshot: AuthSessionSnapshot = {
      access_token: data.attributes.access_token,
      expires_at: Date.now() + data.attributes.access_expires_in * 1000,
      user_id: data.relationships.subject.data.id,
    }

    dispatch(setV3SessionSlice(snapshot))
    dispatch(getAuthSession()).adoptSnapshot(snapshot)
    await dispatch(writeAccessTokenCookieFromClient(snapshot))
  })
}

export function loginAndStore(params: DistributiveOmit<V3PostAuthLoginParams, 'client_id' | 'client_type' | 'device_id'>) {
  return createThunk(async (dispatch): Promise<void> => {
    const cookies = dispatch(getCookies())
    const { data } = await dispatch(
      callApi(
        postAuthLogin({
          ...params,
          client_id: 'web',
          client_type: 'web',
          device_id: await getOrCreateDeviceId(cookies),
        })
      )
    )
    await dispatch(adoptV3Session(data))
    await dispatch(resolveThunkSelector(profileUserResultSelector))
  })
}

/**
 * Staging/dev test login: mints a JWT session by `user_id` via
 * `/api/v3/auth/test_login` — no legacy access token involved. Throws if the
 * request fails.
 */
export function testLoginAndStore(userId: string) {
  return createThunk(async (dispatch): Promise<void> => {
    const cookies = dispatch(getCookies())
    const { data } = await dispatch(
      callApi(
        postAuthTestLogin({
          user_id: userId,
          client_id: 'web',
          client_type: 'web',
          device_id: await getOrCreateDeviceId(cookies),
        })
      )
    )
    await dispatch(adoptV3Session(data))
  })
}

/** Re-persist the session cookie so its expiration slides forward on each visit. */
export function refreshSessionCookie() {
  return createThunk(async dispatch => {
    const cookies = dispatch(getCookies())
    const session = await extractSession(cookies)
    if (session) await persistSession(cookies, session, false)
  })
}

/**
 * Exits supervision: drops the in-memory supervision slice, then clears the
 * httpOnly supervision cookie. State is cleared first so the cookie-clear request
 * authenticates as the main account, not the (possibly rejected) supervision token.
 */
export function clearSupervision() {
  return createThunk(async dispatch => {
    dispatch(clearSupervisionSlice())
    await dispatch(clearSupervisionCookieFromClient())
  })
}

/**
 * Logs the user out. While supervising, this only exits supervision (drops the
 * `kidsout__supervision` layer) and returns to the main account, which lives in
 * its own cookies and is left untouched. Otherwise it tears down the main
 * session and revokes its token.
 */
export function destroySession() {
  return createThunk(async (dispatch, getState) => {
    if (isSupervisedSelector(getState())) {
      // Revoke the supervised (impersonation) session — the auth manager is now
      // supervision-aware, so this uses the supervision token — then drop the
      // supervision layer, leaving the main account's cookies intact.
      await Promise.all([
        dispatch(callApi(deleteAuthSessionsCurrent())).catch(() => {
          // Best-effort: do not block exiting supervision on a v3 endpoint failure
        }),
        dispatch(getFirebaseMessagingManager())
          .then(m => m?.unsubscribe())
          .catch(() => {
            // Best-effort: push unsubscribe must never block or break logout
          }),
      ])
      await dispatch(clearSupervision())
      dispatch(clearFirebaseToken())
      return
    }

    // Past the supervised branch, so this is a real logout: everything from here is teardown.
    dispatch(markSessionTeardown())

    const cookies = dispatch(getCookies())
    const hasV3Session = !!v3SessionSelector(getState())

    {
      const deleteV3SessionPromise = !hasV3Session
        ? Promise.resolve()
        : dispatch(callApi(deleteAuthSessionsCurrent())).catch(() => {
            // Best-effort: do not block logout on v3 endpoint failure
          })

      const unsubscribeFirebasePromise = dispatch(getFirebaseMessagingManager())
        .then(m => m?.unsubscribe())
        .catch(() => {
          // Best-effort: push unsubscribe must never block or break logout
        })

      await Promise.all([deleteV3SessionPromise, unsubscribeFirebasePromise])
    }

    // Await all cookie clears before resolving: the logout flow reloads the page
    // right after this thunk settles, and a lingering kidsout_session, kidsout__at
    // or kidsout_rt would let the server re-establish the session on reload.
    await Promise.all([clearSession(cookies), dispatch(clearAccessTokenCookieFromClient()), dispatch(clearRefreshTokenCookieFromClient())])
    dispatch(clearSupervisionSlice())
    dispatch(clearV3SessionSlice())
    dispatch(clearFirebaseToken())
    dispatch(getAmplitude()).logout()
  })
}

/** Requests an SMS login/bind code via v3, tracking the same Amplitude events the v2 descriptor middleware used to emit. */
export function requestPhoneCode(phone: string, purpose: V3PhoneRequestPurpose) {
  return createThunk(async dispatch => {
    try {
      const result = await dispatch(callApi(postAuthPhoneRequest(phone, purpose)))
      dispatch(getAnalyticsRouter()).send(phoneVerificationEvent(purpose))
      return result
    } catch (error) {
      dispatch(getAnalyticsRouter()).send(phoneVerificationFailEvent(purpose))
      throw error
    }
  })
}

/** Log in as whichever test user the server allows for that side — the ids are its, not ours. */
export function restoreTestUserOfType(type: 'parent' | 'sitter') {
  return createThunk(async dispatch => {
    const users = await dispatch(resolveThunkSelector(testLoginUsersResultSelector))
    const user = users.find(u => u.type === type)
    if (!user) throw new Error(`restoreTestUserOfType: app_config allows no ${type} test user`)
    return await dispatch(restoreTestUser(user.id))
  })
}

export function restoreTestUser(userId: string) {
  return createThunk(async dispatch => {
    await dispatch(testLoginAndStore(userId))
    await dispatch(resolveThunkSelector(profileUserResultSelector))
  })
}

/**
 * Clears the session whose token was just rejected (401). While supervising,
 * only the supervision layer is dropped (back to the main account); otherwise
 * the v3 session is cleared.
 */
export function clearInvalidSession() {
  return createThunk((dispatch, getState) => {
    if (isSupervisedSelector(getState())) {
      void dispatch(clearSupervision())
      return
    }
    dispatch(clearSupervisionSlice())
    dispatch(clearV3SessionSlice())
    dispatch(markSessionLost())
  })
}

/**
 * Cleanup after the auth manager declares the session lost (token rejected with
 * no refresh material). Unlike destroySession nothing is revoked API-side — the
 * credential is already dead — and redux state is left intact: the caller
 * reloads the page, and clearing slices before the reload lands re-renders
 * auth-requiring screens into a crash (FRONTEND-900). Only cookies are dropped,
 * so the reload cannot resurrect the dead session.
 */
export function teardownLostSession() {
  return createThunk(async (dispatch, getState) => {
    const cookies = dispatch(getCookies())
    // The reload waits on this, so a cleanup that never settles must not hold it: the push manager
    // only resolves once a signed-in profile has loaded, and a page stuck here keeps refreshing.
    await Promise.race([
      Promise.allSettled([
        clearSession(cookies),
        dispatch(clearAccessTokenCookieFromClient()),
        dispatch(clearRefreshTokenCookieFromClient()),
        isSupervisedSelector(getState()) ? dispatch(clearSupervisionCookieFromClient()) : Promise.resolve(),
        dispatch(getFirebaseMessagingManager()).then(m => m?.unsubscribe()),
      ]),
      sleep(LOST_SESSION_TEARDOWN_DEADLINE_MS),
    ])
    dispatch(getAmplitude()).logout()
  })
}

export function destroyUser() {
  return createThunk(async (dispatch, getState) => {
    const id = profileUserSelector(getState())?.id
    if (!id) return
    await dispatch(callV2Api(deleteUsersById(id)))
    await dispatch(destroySession())
  })
}

type DistributiveOmit<T, K extends keyof any> = T extends unknown ? Omit<T, K> : never

const LOST_SESSION_TEARDOWN_DEADLINE_MS = 3_000

const phoneVerificationEvent = (purpose: V3PhoneRequestPurpose) =>
  analyticsEvent(() => ({
    // A verification code was requested. {purpose} says what for; every code goes by SMS now.
    type: 'phone_verification',
    data: { purpose, delivery: 'sms' },
  })).set('amplitude')

const phoneVerificationFailEvent = (purpose: V3PhoneRequestPurpose) =>
  analyticsEvent(() => ({
    // A verification code could not be sent.
    type: 'phone_verification_fail',
    data: { purpose, delivery: 'sms' },
  })).set('amplitude')
